// Typed PUBLIC client-env boundary.
//
// WHY THIS EXISTS (security):
// Vite inlines `import.meta.env.VITE_X` into the browser bundle. A STATIC member
// access (`import.meta.env.VITE_FOO`) inlines ONLY that one variable. A DYNAMIC
// access — `import.meta.env[name]` or `(import.meta.env as Record<string, …>)[name]`
// — forces Vite to materialize the ENTIRE `VITE_*` env object into the bundle, so
// EVERY VITE_-prefixed value (including any that should never reach the browser)
// gets shipped to clients. That whole-env materialization is exactly how a
// `VITE_`-prefixed secret leaked into the public assets.
//
// This module is the single place the client reads env. Each value below is a
// STATIC `import.meta.env.VITE_X` reference, so only these explicitly
// allow-listed PUBLIC variables are ever inlined. The runtime lookup in
// `publicEnv()` indexes a plain frozen object — NEVER `import.meta.env` directly
// — so it cannot trigger full-env materialization.
//
// HARD RULE: only intentionally PUBLIC values may appear here (feature flags,
// the Supabase URL, the anon/publishable key, remote-config URLs). NEVER add a
// service_role / secret / privileged credential. Server-only secrets are read by
// Node scripts from `process.env.SUPABASE_SECRET_KEY` (no `VITE_` prefix) and
// must never be referenced by client code.

const PUBLIC_ENV = {
  VITE_BLOCK_LIBRARY: import.meta.env.VITE_BLOCK_LIBRARY,
  VITE_BLOCK_LIBRARY_CONFIG_URL: import.meta.env.VITE_BLOCK_LIBRARY_CONFIG_URL,
  VITE_HYPOTHESIS_ENGINE: import.meta.env.VITE_HYPOTHESIS_ENGINE,
  VITE_HYPOTHESIS_PLAYGROUND: import.meta.env.VITE_HYPOTHESIS_PLAYGROUND,
  VITE_VALIDATION_ENGINE: import.meta.env.VITE_VALIDATION_ENGINE,
  VITE_VALIDATION_PLAYGROUND: import.meta.env.VITE_VALIDATION_PLAYGROUND,
  VITE_DISCOVERY: import.meta.env.VITE_DISCOVERY,
  // Phase 3B pilot controls. VITE_DISCOVERY_COHORT=on → enforce the pilot-cohort
  // gate (default-deny). VITE_DISCOVERY_PERSISTENCE=durable → production pilot MUST
  // use the authorized Postgres repo (fail closed if not configured; never local).
  VITE_DISCOVERY_COHORT: import.meta.env.VITE_DISCOVERY_COHORT,
  VITE_DISCOVERY_PERSISTENCE: import.meta.env.VITE_DISCOVERY_PERSISTENCE,
  VITE_PINE_IMPORTER_BETA: import.meta.env.VITE_PINE_IMPORTER_BETA,
  VITE_PINE_IMPORTER_ALLOWLIST: import.meta.env.VITE_PINE_IMPORTER_ALLOWLIST,
  VITE_PINE_REMOTE_CONFIG_URL: import.meta.env.VITE_PINE_REMOTE_CONFIG_URL,
  VITE_FUNDAMENTALS_DIMENSION: import.meta.env.VITE_FUNDAMENTALS_DIMENSION,
  VITE_REVENUE_FUNNEL: import.meta.env.VITE_REVENUE_FUNNEL,
  // Curated Sharadar US-equity markets in the picker (default OFF; see
  // equityMarketsFlag.ts — flip to 'on' only under the commercial license).
  VITE_EQUITY_MARKETS: import.meta.env.VITE_EQUITY_MARKETS,
  // Build stamp for Reality Check report provenance (public by nature).
  VITE_GIT_COMMIT: import.meta.env.VITE_GIT_COMMIT,
  // Reality Check feature flags (public by nature — route/section gates only).
  VITE_REALITY_CHECK_DEMO: import.meta.env.VITE_REALITY_CHECK_DEMO,
  VITE_REALITY_CHECK_REPORT: import.meta.env.VITE_REALITY_CHECK_REPORT,
} as const;

export type PublicEnvKey = keyof typeof PUBLIC_ENV;

/**
 * Read an allow-listed PUBLIC client env var. Returns `undefined` if unset.
 *
 * The lookup is on the plain `PUBLIC_ENV` object (whose values were already
 * statically inlined) — never on `import.meta.env` directly — so it can never
 * cause Vite to materialize the full env object into the bundle.
 */
export function publicEnv(name: PublicEnvKey): string | undefined {
  const v = PUBLIC_ENV[name];
  return typeof v === 'string' ? v : undefined;
}
